import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const NoticesPage = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotice, setSelectedNotice] = useState(null);

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/popupMessages');
        const data = await response.json();

        if (data.message === 'Popup messages fetched successfully') {
          setNotices(data.popupMessages);
        } else {
          toast.error('Failed to fetch notices');
        }
      } catch (error) {
        console.error('Error fetching notices:', error);
        toast.error('Failed to fetch notices');
      } finally {
        setLoading(false);
      }
    };

    fetchNotices();
  }, []);

  const handleNoticeClick = (notice) => {
    setSelectedNotice(notice);
  };

  const handleCloseViewer = () => {
    setSelectedNotice(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold">Notices</h1>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
            {notices.map((notice) => (
              <div
                key={notice.popup_id}
                className="bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-shadow duration-300 w-full max-w-sm"
                onClick={() => handleNoticeClick(notice)}
              >
                <div className="aspect-[1/1.4142] relative"> {/* A4 aspect ratio */}
                  {notice.image_url ? (
                    <img
                      src={notice.image_url}
                      alt={notice.alt_text || notice.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.error('Image failed to load:', notice.image_url);
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                      <span className="text-gray-400">No Image</span>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold mb-2">{notice.title}</h3>
                  <p className="text-gray-600 text-sm line-clamp-2">{notice.content}</p>
                  <p className="text-gray-500 text-xs mt-2">
                    {new Date(notice.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Notice Viewer */}
        {selectedNotice && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Close button */}
              <button
                onClick={handleCloseViewer}
                className="absolute top-4 right-4 text-white hover:text-gray-300"
              >
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Notice content */}
              <div className="bg-white rounded-lg shadow-xl h-[90vh] mx-4 overflow-hidden">
                <div className="h-full w-auto flex flex-col">
                  {selectedNotice.image_url ? (
                    <div className="flex-1 min-h-0 relative">
                      <img
                        src={selectedNotice.image_url}
                        alt={selectedNotice.alt_text || selectedNotice.title}
                        className="h-full w-auto object-contain"
                        onError={(e) => {
                          console.error('Image failed to load:', selectedNotice.image_url);
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="flex-1 min-h-0 bg-gray-100 flex items-center justify-center">
                      <span className="text-gray-400">No Image</span>
                    </div>
                  )}
                  <div className="p-6 bg-white">
                    <h2 className="text-2xl font-bold mb-4">{selectedNotice.title}</h2>
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedNotice.content}</p>
                    <p className="text-gray-500 text-sm mt-4">
                      Posted on: {new Date(selectedNotice.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NoticesPage; 